import { z } from 'zod';
import {
    DateService,
    DateTimeUtcService,
    DateTimeUtils,
    ISOFormat,
    WallClockDateService,
} from '@mews-framework/datetime';
import { DateTimeLocalizationInfo } from '@mews-framework/globalization';
import { CustomErrorParams, CustomErrorType } from '../../CustomErrorType';

const DateRangeSchema = z.object({
    startDate: z.string(),
    endDate: z.string(),
});

type DateRange = z.infer<typeof DateRangeSchema>;

const isWallClockDateRangeValid = (
    { startDate, endDate }: DateRange,
    dateTimeLocalizationInfo: DateTimeLocalizationInfo
) =>
    WallClockDateService.isValidString(startDate, dateTimeLocalizationInfo) &&
    WallClockDateService.isValidString(endDate, dateTimeLocalizationInfo);

const wallClockDateRangeToDates = (
    { startDate, endDate }: DateRange,
    dateTimeLocalizationInfo: DateTimeLocalizationInfo,
    timezone: string
) => ({
    startDate: DateService.fromString(startDate, dateTimeLocalizationInfo, timezone),
    endDate: DateService.fromString(endDate, dateTimeLocalizationInfo, timezone),
});

export const wallClock = (dateTimeLocalizationInfo: DateTimeLocalizationInfo, timezone: string) =>
    DateRangeSchema.passthrough().refine(
        (value) => {
            if (isWallClockDateRangeValid(value, dateTimeLocalizationInfo)) {
                const { startDate, endDate } = wallClockDateRangeToDates(value, dateTimeLocalizationInfo, timezone);
                return DateService.isLessOrEqual(startDate, endDate);
            }
        },
        { params: { errorType: CustomErrorType.DateRange } satisfies CustomErrorParams }
    );

const isIsoDateRangeValid = ({ startDate, endDate }: DateRange) =>
    DateTimeUtils.isValidDateIsoString(startDate) && DateTimeUtils.isValidDateIsoString(endDate);

const isoDateRangeToDates = ({ startDate, endDate }: DateRange) => ({
    startDate: DateService.fromString(startDate, ISOFormat.Date),
    endDate: DateService.fromString(endDate, ISOFormat.Date),
});

export const iso = DateRangeSchema.passthrough().refine(
    (value) => {
        if (isIsoDateRangeValid(value)) {
            const { startDate, endDate } = isoDateRangeToDates(value);
            return DateService.isLessOrEqual(startDate, endDate);
        }
    },
    { params: { errorType: CustomErrorType.DateRange } satisfies CustomErrorParams }
);

const isUtcDateRangeValid = ({ startDate, endDate }: DateRange) =>
    DateTimeUtcService.isValidString(startDate) && DateTimeUtcService.isValidString(endDate);

const utcDateRangeToDates = ({ startDate, endDate }: DateRange, timezone: string) => ({
    startDate: DateTimeUtils.dateTimeUtcStringToDate(startDate, timezone),
    endDate: DateTimeUtils.dateTimeUtcStringToDate(endDate, timezone),
});

export const utc = (timezone: string) =>
    DateRangeSchema.passthrough().refine(
        (value) => {
            if (isUtcDateRangeValid(value)) {
                const { startDate, endDate } = utcDateRangeToDates(value, timezone);
                return DateService.isLessOrEqual(startDate, endDate);
            }
        },
        { params: { errorType: CustomErrorType.DateRange } satisfies CustomErrorParams }
    );
